import React, { useState, useEffect, useRef } from 'react';
import { fetchPhotoManagementFolders, editPhotoManagementFolderRecord, createPhotoManagementFolderRecord, deletePhotoManagementFolderRecord } from '../services/photoManagementFolders.service'
import {createPhotoManagementFilesRecord, deletePhotoManagementFilesRecord,editPhotoManagementFilesRecord} from '../services/photoManagementFiles.service'
import { postUploadImagesAuth } from '../services/document-s3.service'
import { connect } from 'react-redux';
// Utility functions
const uid = () => Math.random().toString(36).slice(2, 10);
const escapeHtml = (s) => s.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
const toNum = (v) => { const n = parseFloat(v); return isNaN(n) ? NaN : n; };
const humanSize = (n) => {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (n > 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(1)} ${u[i]}`;
};

const stripExt = (name) => name.replace(/\.[^.]+$/, '');
const sanitizeFilename = (filename) => {
    // Remove non-ISO-8859-1 characters and replace with safe alternatives
    return filename
        .replace(/[^\x00-\xff]/g, '') // Remove non-ISO-8859-1 characters
        .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace unsafe characters with underscore
        .substring(0, 100); // Limit length
};
const fileToDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

const sumSizes = (folder) => {
    let sum = (folder.files || []).reduce((a, p) => a + (p.sizeInByte || p.size || 0), 0);
    (folder.folders || []).forEach(c => sum += sumSizes(c));
    return sum;
};

const makeCanvasThumb = (color, w, h, text) => new Promise(res => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    g.fillStyle = color;
    g.fillRect(0, 0, w, h);
    g.fillStyle = 'rgba(0,0,0,.25)';
    g.fillRect(0, h - 40, w, 40);
    g.fillStyle = '#fff';
    g.font = '16px Inter, sans-serif';
    g.fillText(text, 10, h - 14);
    res(c.toDataURL('image/png'));
});

// FolderTree Component
function FolderTree({ folder, currentFolder, onSelectFolder, depth = 0, handleDeleteFolder }) {
    const [isExpanded, setIsExpanded] = useState(true);

    const handleClick = () => {
        onSelectFolder(folder);
    };



    return (
        <div>
            <div
                className={`flex items-center gap-2 p-1.5 rounded-sm cursor-pointer select-none hover:bg-[#0d1832] ${folder === currentFolder ? 'bg-[#0C0D14] border border-[#ffffff40]' : ''}`}
                onClick={handleClick}
            >
                <span>{isExpanded ? '📂' : '📁'}</span>
                <span className="flex-1">{folder.name}</span>
                {folder.folderId!=='root'&&<span className='cursor-pointer' onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder._id)(); }}>✖</span>}
            </div>
            {isExpanded && (
                <div className="ml-[18px] border-l border-dashed border-[#233355] pl-2">
                    {(folder.folders || []).map(child => (
                        <FolderTree
                            key={child._id}
                            folder={child}
                            currentFolder={currentFolder}
                            onSelectFolder={onSelectFolder}
                            depth={depth + 1}
                            handleDeleteFolder={handleDeleteFolder}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// PhotoThumb Component
function PhotoThumb({ photo, isSelected, onSelect, onRename, onDelete }) {
    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState(photo.name);
    const inputRef = useRef(null);

    const handleRename = () => {
        setIsRenaming(true);
    };

    const commitRename = () => {
        if (newName.trim()) {
            onRename(photo, newName.trim());
        }
        setIsRenaming(false);
    };

    useEffect(() => {
        if (isRenaming && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isRenaming]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            commitRename();
        } else if (e.key === 'Escape') {
            setIsRenaming(false);
            setNewName(photo.name);
        }
    };

    return (
        <div className={`bg-[#0C0D14] border border-[#ffffff40] rounded-sm overflow-hidden flex flex-col cursor-pointer transition-all ${isSelected ? 'outline outline-2 outline-[#4da3ff]' : ''}`}>
            <div className="w-full bg-[#081226] flex items-center justify-center overflow-hidden" style={{ aspectRatio: '16/10' }}>
                <img src={photo.url} alt={photo.name} onClick={() => onSelect(photo)} className="w-full h-full object-cover" />
            </div>
            <div className="flex items-center gap-1.5 p-2 border-t border-[#1f2f51]">
                {isRenaming ? (
                    <input
                        ref={inputRef}
                        className="w-full bg-[#0a1530] border border-[#29406e] rounded-sm p-1.5 text-[#e6f1ff] font-inherit text-sm focus:outline-none"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={commitRename}
                    />
                ) : (
                    <div className="flex-1 break-words cursor-default" onDoubleClick={handleRename}>
                        {photo.name}
                    </div>
                )}
                <div className="flex gap-1.5">
                    {/* <button className="bg-[#0C0D14] border border-[#ffffff40] text-[#cfe3ff] px-2 py-1 rounded-sm hover:bg-[#1a2540]" onClick={handleRename} title="Rename">✎</button> */}
                    <button className="bg-[#0C0D14] border border-[#ffffff40] text-[#cfe3ff] px-2 py-1 rounded-sm hover:bg-[#1a2540]" onClick={() => onDelete(photo)} title="Delete">🗑</button>
                </div>
            </div>
        </div>
    );
}

// Main Component
const PhotoManagementPanel = (props) => {
    const [fs, setFs] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [saveMsg, setSaveMsg] = useState('');
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [detailForm, setDetailForm] = useState({ name: '', latitude: '', longitude: '', description: '', takenAt: '' });
    const fileInputRef = useRef(null);



    const updateFs = async (init=false) => {
        let result = await fetchPhotoManagementFolders(props.token?.token, { folderId: 'root' }, {})
        let rootFolder = result?.result?.[0] || null

        setFs(rootFolder);

        if(init) {
            setCurrentFolder(rootFolder);
            setSelectedPhoto(null);
        }else{
            let currentFolderId = currentFolder._id || 'root';
            let newCurrentFolder = findFolderById(rootFolder, currentFolderId);
            setCurrentFolder(newCurrentFolder||rootFolder);
            let selectedPhotoId = selectedPhoto?._id || null;
            setSelectedPhoto(findPhotoById(newCurrentFolder, selectedPhotoId));
        }
    }

    const findPhotoById = (folder, id) => {
        if (!id) return null;
        for (const photo of folder.files || []) {
            if (photo._id === id) return photo;
        }
        for (const sub of folder.folders || []) {
            const found = findPhotoById(sub, id);
            if (found) return found;
        }
        return null;
    }
    const findFolderById = (folder, id) => {
        if ((folder._id || 'root') === id) return folder;
        for (const sub of folder.folders || []) {
            const found = findFolderById(sub, id);
            if (found) return found;
        }
        return null;
    }
    // Initialize file system
    useEffect(() => {
        updateFs(true);
    }, []);



    const handleSelectFolder = (folder) => {
        setCurrentFolder(folder);
        setSelectedPhoto(null);
    };

    const handleSelectPhoto = (photo) => {
        setSelectedPhoto(photo);
        setDetailForm({
            name: photo.name || '',
            latitude: photo.latitude ?? '',
            longitude: photo.longitude ?? '',
            description: photo.description ?? '',
            takenAt: photo.takenAt ?? ''
        });
    };

    const handleRenamePhoto = (photo, newName) => {
        photo.name = newName;
        setSelectedPhoto({ ...selectedPhoto });
        setFs({ ...fs });
    };

    const handleNewFolder = async () => {
        const name = window.prompt('Folder name?', 'New Folder');
        if (!name) return;

        await createPhotoManagementFolderRecord(props.token?.token, { name, folderId: currentFolder._id || 'root' })
        await updateFs()
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileInputChange = async (e) => {
        const files = e.target.files;
        if (!files) return;
        await handleFiles(Array.from(files));
        // Reset the file input so the same file can be uploaded again
        e.target.value = '';
    };

    const handleFiles = async (files) => {
        for (const file of files) {
            if (!file.type.startsWith('image/')) continue;

            try {
                let formData = new FormData();
                const originalName = stripExt(file.name);
                const safeName = Date.now() + '-' + Math.random().toString(36).substring(2, 15)
                
                formData.append('file', file, file.name);
                formData.append('json', JSON.stringify({}));

                let uploadResult = await postUploadImagesAuth('photo-management', safeName, formData, props.token?.token)
                let url = uploadResult?.result || null

                if (!url) {
                    alert('Upload failed for ' + file.name);
                    continue;
                }
                const photo = {
                    name: originalName, // Use original name for display
                    url,
                    sizeInByte: file.size,
                    takenAt: '',
                    latitude: null,
                    longitude: null,
                    description: '',
                    folderId: currentFolder._id || 'root',
                };

                let result = await createPhotoManagementFilesRecord(props.token?.token, photo)
            } catch (error) {
                console.error('Error uploading file:', error);
                alert('Error uploading ' + file.name + ': ' + error.message);
            }
        }

        await updateFs();
    };

    const handleSaveMeta = async() => {
        if (!selectedPhoto) {
            setSaveMsg('No photo selected');
            return;
        }
        selectedPhoto.name = detailForm.name || selectedPhoto.name;
        selectedPhoto.latitude = detailForm.latitude
        selectedPhoto.longitude = detailForm.longitude
        selectedPhoto.description = detailForm.description;
        selectedPhoto.takenAt = detailForm.takenAt;
        setSaveMsg('Saved ✓');
        setTimeout(() => setSaveMsg(''), 1200);

        let data={
            name: selectedPhoto.name,
            latitude: selectedPhoto.latitude,
            longitude: selectedPhoto.longitude,
            description: selectedPhoto.description,
            takenAt: selectedPhoto.takenAt   
        }
        await editPhotoManagementFilesRecord(props.token?.token, selectedPhoto._id, data)
        await updateFs();
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        await handleFiles(files);
    };

    // Filter photos based on search
    const filteredPhotos = currentFolder && currentFolder.files
        ? currentFolder.files.filter(p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    const totalSize = fs ? sumSizes(fs) : 0;

    const handleDeleteFolder = (folderId) => async () => {
        if (!window.confirm('Delete this folder and all its contents?')) return;
        await deletePhotoManagementFolderRecord(props.token?.token, folderId);
        await updateFs();
    };
    const handleDeletePhoto = async (photo) => {
        if (!window.confirm('Delete this photo?')) return;
        await deletePhotoManagementFilesRecord(props.token?.token, photo._id);
        await updateFs();
    };


    if (!fs || !currentFolder) {
        return <div className="photo-management-panel">Loading...</div>;
    }

    return (
        <div className="w-full h-full bg-[#0C0D14] text-[#e6f1ff] font-sans text-sm leading-loose box-border flex flex-col">
            <div className="grid h-full grid-cols-[280px_1fr_360px] gap-2.5 p-2.5 bg-[#0C0D14]">
                {/* Left Panel: Folders + Preview */}
                <section className="flex flex-col gap-2.5 min-h-0">
                    <div className="bg-gradient-to-b from-[#0C0D14] to-[#0C0D14] border border-[#ffffff40] rounded-sm overflow-hidden flex flex-col min-h-0">
                        <header className="border-b border-[#ffffff40] px-3 py-2.5 flex items-center justify-between">
                            <h3 className="m-0 text-sm">Folders</h3>
                            <div className="flex gap-1.5">
                                <button className="bg-[#0C0D14] border border-[#ffffff40] text-[#cfe3ff] px-2.5 py-2 rounded-sm min-w-[36px] cursor-pointer hover:bg-[#1a2540]" onClick={handleNewFolder}>
                                    ＋ Folder
                                </button>
                            </div>
                        </header>
                        <div className="overflow-auto px-2 py-1.5">
                            <FolderTree
                                folder={fs}
                                currentFolder={currentFolder}
                                onSelectFolder={handleSelectFolder}
                                handleDeleteFolder={handleDeleteFolder}
                            />
                        </div>
                    </div>
                    <div className="bg-gradient-to-b from-[#0C0D14] to-[#0C0D14] border border-[#ffffff40] rounded-sm overflow-hidden flex flex-col min-h-0">
                        <header className="border-b border-[#ffffff40] px-3 py-2.5"><h3 className="m-0 text-sm">Preview</h3></header>
                        <div className="flex-1 flex">
                            {selectedPhoto ? (
                                <img src={selectedPhoto.url} alt="Preview" className="w-full h-full object-contain bg-[#0C0D14]" />
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-[#9fb2c7]">
                                    No photo selected
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Center Panel: Thumbnails */}
                <section className="bg-gradient-to-b from-[#0C0D14] to-[#0C0D14] border border-[#ffffff40] rounded-sm overflow-hidden flex flex-col min-h-0">
                    <div className="p-2.5 flex gap-2 flex-wrap items-center border-b border-[#ffffff40]">
                        <input
                            placeholder="Search by name"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                        />
                        <button className="bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] px-2.5 py-2 rounded-sm cursor-pointer hover:bg-[#1a2540]" onClick={handleUploadClick}>
                            Upload Photos
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleFileInputChange}
                        />
                        <span className="text-[#9fb2c7]">Drag & drop files into the grid</span>
                    </div>
                    <div
                        className={`flex-1 min-h-0 relative overflow-hidden ${isDraggingOver ? 'outline outline-[2px] outline-dashed outline-[#2a6bd6] outline-offset-[-8px]' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {isDraggingOver && (
                            <div className="absolute inset-0 flex items-center justify-center bg-[rgba(20,30,55,0.6)] backdrop-blur-[2px] text-[#cfe3ff] font-semibold pointer-events-none z-10">
                                Drop images to upload
                            </div>
                        )}
                        <div className="h-full overflow-auto p-3.5 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))]">
                            {filteredPhotos.map(photo => (
                                <PhotoThumb
                                    key={photo._id}
                                    photo={photo}
                                    isSelected={selectedPhoto && selectedPhoto._id === photo._id}
                                    onSelect={handleSelectPhoto}
                                    onRename={handleRenamePhoto}
                                    onDelete={handleDeletePhoto}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Right Panel: Details */}
                <section className="bg-gradient-to-b from-[#0C0D14] to-[#0C0D14] border border-[#ffffff40] rounded-sm overflow-hidden flex flex-col">
                    <header className="border-b border-[#ffffff40] px-3 py-2.5"><h3 className="m-0 text-sm">Details</h3></header>
                    {selectedPhoto ? 
                    <div className="p-3 overflow-auto flex flex-col gap-2.5">
                        <div className="mb-2.5">
                            <label className="block text-[#9fb2c7] text-xs mb-1.5">Photo name</label>
                            <input
                                placeholder="File name"
                                value={detailForm.name}
                                onChange={(e) => setDetailForm({ ...detailForm, name: e.target.value })}
                                className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                            <div className="mb-2.5">
                                <label className="block text-[#9fb2c7] text-xs mb-1.5">Latitude</label>
                                <input
                                    type="number"
                                    step="0.000001"
                                    placeholder="e.g. 22.396428"
                                    value={detailForm.latitude}
                                    onChange={(e) => setDetailForm({ ...detailForm, latitude: e.target.value })}
                                    className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                                />
                            </div>
                            <div className="mb-2.5">
                                <label className="block text-[#9fb2c7] text-xs mb-1.5">Longitude</label>
                                <input
                                    type="number"
                                    step="0.000001"
                                    placeholder="e.g. 114.109497"
                                    value={detailForm.longitude}
                                    onChange={(e) => setDetailForm({ ...detailForm, longitude: e.target.value })}
                                    className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                                />
                            </div>
                        </div>
                        <div className="mb-2.5">
                            <label className="block text-[#9fb2c7] text-xs mb-1.5">Description</label>
                            <textarea
                                rows="3"
                                placeholder="Notes"
                                value={detailForm.description}
                                onChange={(e) => setDetailForm({ ...detailForm, description: e.target.value })}
                                className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2.5">
                            <div className="mb-2.5">
                                <label className="block text-[#9fb2c7] text-xs mb-1.5">Taken at</label>
                                <input
                                    type="datetime-local"
                                    value={detailForm.takenAt}
                                    onChange={(e) => setDetailForm({ ...detailForm, takenAt: e.target.value })}
                                    className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                                />
                            </div>
                            <div className="mb-2.5">
                                <label className="block text-[#9fb2c7] text-xs mb-1.5">Size</label>
                                <input
                                    readOnly
                                    value={selectedPhoto ? humanSize(selectedPhoto.sizeInByte || 0) : ''}
                                    className="w-full bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] rounded-sm p-2 font-inherit text-sm focus:outline-none focus:border-[#4da3ff] focus:ring-2 focus:ring-[rgba(77,163,255,0.1)]"
                                />
                            </div>
                        </div>
                        <div className="h-px bg-[#ffffff40] my-2"></div>
                        <div className="flex gap-2 flex-wrap">
                            <button className="bg-[#0C0D14] border border-[#ffffff40] text-[#e6f1ff] px-2.5 py-2 rounded-sm cursor-pointer hover:bg-[#1a2540]" onClick={handleSaveMeta}>
                                Save
                            </button>
                            <button
                                className="bg-[#2a0f13] border border-[#6b1f2b] text-[#ffb3c1] px-2.5 py-2 rounded-sm cursor-pointer hover:bg-[#3d1520]"
                                onClick={() => {
                                    if (selectedPhoto) handleDeletePhoto(selectedPhoto);
                                }}
                            >
                                Delete
                            </button>
                            <span className="text-[#9fb2c7]">
                                {saveMsg}
                            </span>
                        </div>
                    </div>
                    : null}
                </section>
            </div>

            {/* Storage badge */}
            <div className="fixed bottom-5 right-5 px-3 py-2 border border-[#ffffff40] rounded-full text-xs bg-[#0C0D14]">
                Storage {humanSize(totalSize)}
            </div>
        </div>
    );
}



export default connect((store) => {
    return {
        token: store.tokenReducer,
        control: store.controlReducer,
    }
})(PhotoManagementPanel)
